import {Loc} from 'main.core';
import {EventEmitter} from 'main.core.events';
import {Outline} from 'ui.icon-set.api.vue';

import {Core} from 'tasks.v2.core';
import {EventName} from 'tasks.v2.const';
import {taskService} from 'tasks.v2.provider.service.task-service';
import {resultService} from 'tasks.v2.provider.service.result-service';

/**
 * ES6-constructor-friendly single-level subclass (one Reflect.construct hop; avoids babel inherits+.call on TaskCommentsMessageMenu).
 *
 * @param {function} SuperCtor
 * @param {Record<string, Function>} protoProps
 * @returns {function}
 */
export function subClassEs6(SuperCtor, protoProps)
{
    function Derived()
    {
        const argsLen = arguments.length;
        var args = new Array(argsLen);
        for (var _key = 0; _key < argsLen; _key++)
        {
            args[_key] = arguments[_key];
        }

        return Reflect.construct(SuperCtor, args, Derived);
    }

    Derived.prototype = Object.create(SuperCtor.prototype);
    Derived.prototype.constructor = Derived;
    Object.setPrototypeOf(Derived, SuperCtor);
    Object.assign(Derived.prototype, protoProps);

    return Derived;
}

/**
 * Prototype fragments mirroring tasks.v2 TaskFullCardMessageMenu behaviour (excluding getTaskId: supplied by Restricted overlay).
 *
 * @returns {Record<string, Function>}
 */
export function getFullCardTaskCommentsMenuMethods()
{
    return {
        getAddResultItem: function getAddResultItem()
        {
            if (
                this.isDeletedMessage()
                || this._htstechIsSystemMessage()
                || !this._htstechShouldShowAddResult()
            )
            {
                return null;
            }

            return {
                title: Loc.getMessage('TASKS_V2_TASK_FULL_CARD_MESSAGE_ADD_RESULT'),
                icon: Outline.FLAG,
                onClick: () => {
                    EventEmitter.emit(EventName.AddResultFromChat, {
                        taskId: this.getTaskId(),
                        messageId: this.context.id,
                        text: this.context.text,
                        authorId: this._htstechGetUserId()
                    });

                    this.close();
                }
            };
        },

        getRemoveResultItem: function getRemoveResultItem()
        {
            if (
                this.isDeletedMessage()
                || this._htstechIsSystemMessage()
                || !this._htstechShouldShowRemoveResult()
            )
            {
                return null;
            }

            return {
                title: Loc.getMessage('TASKS_V2_TASK_FULL_CARD_MESSAGE_DELETE_RESULT'),
                icon: Outline.FLAG_WITH_CROSS,
                onClick: () => {
                    EventEmitter.emit(EventName.DeleteResultFromChat, {
                        taskId: this.getTaskId(),
                        resultId: this._htstechGetMessageResultIdForCurrentUser(),
                    });

                    this.close();
                }
            };
        },

        _htstechHasTasksStore: function _htstechHasTasksStore()
        {
            const store = Core?.getStore?.();
            return Boolean(store && store.getters);
        },

        _htstechShouldShowAddResult: function _htstechShouldShowAddResult()
        {
            const task = this._htstechGetTask();
            if (!task)
            {
                return false;
            }

            return this._htstechGetMessageResultIdForCurrentUser() === 0;
        },

        _htstechIsSystemMessage: function _htstechIsSystemMessage()
        {
            return this.context.authorId === 0;
        },

        _htstechShouldShowRemoveResult: function _htstechShouldShowRemoveResult()
        {
            const task = this._htstechGetTask();
            if (!task)
            {
                return false;
            }

            return this._htstechGetMessageResultIdForCurrentUser() > 0;
        },

        _htstechGetMessageResultIdForCurrentUser: function _htstechGetMessageResultIdForCurrentUser()
        {
            const task = this._htstechGetTask();
            if (!task)
            {
                return 0;
            }

            if (!this._htstechHasTasksStore())
            {
                return 0;
            }

            const map = task?.resultsMessageMap || {};
            const messageId = this.context.id;

            for (const [resultId, boundMessageId] of Object.entries(map))
            {
                if (boundMessageId !== null && Number(boundMessageId) === Number(messageId))
                {
                    const messageResult = resultService.getStoreResult(Number(resultId));

                    if (messageResult !== null && messageResult.author.id === this._htstechGetUserId())
                    {
                        return messageResult.id;
                    }
                }
            }

            return 0;
        },

        _htstechGetTask: function _htstechGetTask()
        {
            if (!this._htstechHasTasksStore())
            {
                return null;
            }

            return taskService.getStoreTask(this.getTaskId());
        },

        _htstechGetUserId: function _htstechGetUserId()
        {
            return Core.getParams().currentUser.id;
        }
    };
}
